import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

import { onboardingState } from '../../../redux/slices/detSlice';
import { setGuestVerificationLazyLoad } from '../../../redux/slices/sessionSlice';
import Swal from 'sweetalert2';

interface CaptureFaceProps {
	incode: any;
	type: 'selfie' | 'login';
	onComplete: (result: {
		success: boolean;
		stage: 'selfie' | 'face';
		data?: any;
		error?: any;
		confidence?: any;
		existingUser?: any;
	}) => void;
	onErrorFace?: (error: {
		success: boolean;
		stage: 'selfie' | 'face';
		data?: any;
		error?: any;
	}) => void;
	initializeSDK: () => Promise<boolean>;
}

const CaptureFace = ({
	incode,
	onComplete,
	onErrorFace,
	type,
	initializeSDK,
}: CaptureFaceProps) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const hasStartedRef = useRef(false);
	const hasCompletedRef = useRef(false);
	const dispatch = useAppDispatch();
	const { onboardingToken } = useAppSelector(onboardingState);
	const [step, setStep] = useState<'init' | 'selfie' | 'processing' | 'done'>(
		'init'
	);
	const [message, setMessage] = useState('Preparing camera...');
	const [isProcessing, setIsProcessing] = useState(false);
	const [hideInstructions, setHideInstructions] = useState(false);

	// useEffect(() => {
	// 	const timer = setTimeout(() => {
	// 		const btn = document.querySelector(
	// 			'.IncodeCaptureSelfieButtonContainer button'
	// 		) as HTMLButtonElement;
	// 		if (btn) {
	// 			btn.style.display = 'block';
	// 		}
	// 	}, 30000); // 30 sec

	// 	return () => clearTimeout(timer);
	// }, []);

	/* ---------------------------------------------------
     🚀 Auto Start When Component Loads
  --------------------------------------------------- */
	useEffect(() => {
		if (incode && containerRef.current) {
			dispatch(setGuestVerificationLazyLoad(true));
			const start = async () => {
				try {
					const success = await initializeSDK();
					if (success) {
						setTimeout(() => {
							dispatch(setGuestVerificationLazyLoad(false));
						}, 2000);
						startFaceCapture();
					} else {
						dispatch(setGuestVerificationLazyLoad(false));
					}
				} catch (e) {
					console.error('Incode render error', e);
					dispatch(setGuestVerificationLazyLoad(false));
				}
			};
			start();
		}

		return () => {
			hasStartedRef.current = false;
			hasCompletedRef.current = false;

			try {
				incode?.clean?.();
			} catch (err) {
				console.log('Safe CaptureFace SDK cleanup failed', err);
			}

			const container = containerRef.current;
			if (!container) return;

			container
				.querySelectorAll('video')
				.forEach((videoEl: HTMLVideoElement) => {
					const stream = videoEl.srcObject;
					if (stream instanceof MediaStream) {
						stream.getTracks().forEach((track) => track.stop());
					}
					videoEl.srcObject = null;
				});

			container.innerHTML = '';
		};
	}, [incode, initializeSDK]);

	// useEffect(() => {
	// 	dispatch(setGuestVerificationLazyLoad(isProcessing));
	// 	return () => {
	// 		dispatch(setGuestVerificationLazyLoad(false));
	// 	};
	// }, [isProcessing, dispatch]);

	/* ---------------------------------------------------
     1️⃣ Capture Selfie
  --------------------------------------------------- */
	const startFaceCapture = () => {
		if (hasStartedRef.current || !containerRef.current) {
			return;
		}

		hasStartedRef.current = true;
		setStep('selfie');
		setMessage('Please position your face inside the frame');

		try {
			incode.renderCaptureFace(containerRef.current, {
				session: { token: onboardingToken },
				assistedOnboarding: false,
				hatCheckEnabled: false,
				lensesCheckEnabled: true,
				maskCheckEnabled: true,
				eyesClosedCheckEnabled: true,
				showCustomCameraPermissionScreen: true,

				numberOfTries: 3,
				timeout: 30000,
				onSuccess: async (res: any) => {
					if (hasCompletedRef.current) {
						return;
					}

					hasCompletedRef.current = true;
					console.log('renderCamera onSuccess triggered = ', res);
					// setIsProcessing(true);
					setHideInstructions(true);
					setMessage('Face captured. Verifying...');
					// Dispatch loading state early to avoid delay in showing the global loader
					dispatch(setGuestVerificationLazyLoad(true));
					// Call processFace after successful capture
					await faceMatch();
				},
				onError: (err: any) => {
					hasStartedRef.current = false;
					hasCompletedRef.current = false;
					setMessage('Face capture failed. Please try again.');
					// onComplete({
					// 	success: false,
					// 	stage: 'selfie',
					// 	error: err,
					// });
					setIsProcessing(false);
					onErrorFace?.({
						success: false,
						stage: 'selfie',
						error: err,
					});
				},
			});
			// incode.renderCamera('selfie', containerRef.current, {
			// 	assistedOnboarding: false,
			// 	hatCheckEnabled: false,
			// 	lensesCheckEnabled: true,
			// 	maskCheckEnabled: true,
			// 	eyesClosedCheckEnabled: true,
			// 	showCustomCameraPermissionScreen: true,
			// 	token: onboardingToken, // Don't forget to pass the token!
			// 	numberOfTries: 3,
			// 	timeout: 30000,
			// 	onSuccess: async (res: any) => {
			// 		if (hasCompletedRef.current) {
			// 			return;
			// 		}

			// 		hasCompletedRef.current = true;
			// 		console.log('renderCamera onSuccess triggered = ', res);
			// 		// setIsProcessing(true);
			// 		setHideInstructions(true);
			// 		setMessage('Face captured. Verifying...');
			// 		// Dispatch loading state early to avoid delay in showing the global loader
			// 		dispatch(setGuestVerificationLazyLoad(true));
			// 		// Call processFace after successful capture
			// 		await faceMatch();
			// 	},
			// 	onError: (err: any) => {
			// 		hasStartedRef.current = false;
			// 		hasCompletedRef.current = false;
			// 		setMessage('Face capture failed. Please try again.');
			// 		// onComplete({
			// 		// 	success: false,
			// 		// 	stage: 'selfie',
			// 		// 	error: err,
			// 		// });
			// 		setIsProcessing(false);
			// 		onErrorFace?.({
			// 			success: false,
			// 			stage: 'selfie',
			// 			error: err,
			// 		});
			// 	},
			// });
		} catch (err: any) {
			console.error('Face capture initialization failed:', err);

			// Check if it's the ML API initialization error
			if (
				err &&
				typeof err.message === 'string' &&
				err.message.includes(
					"Unable to set camera info, cpp API hasn't been initialized"
				)
			) {
				Swal.fire({
					title: 'Error',
					text: 'Some error occurred, try again',
					icon: 'error',
					confirmButtonText: 'OK',
				});
			}

			hasStartedRef.current = false;
			hasCompletedRef.current = false;
			setMessage('Face capture failed. Please try again.');
			setIsProcessing(false);
			onErrorFace?.({
				success: false,
				stage: 'selfie',
				error: err,
			});
		}
	};
	const faceMatch = async () => {
		try {
			setIsProcessing(true);
			setMessage('Processing face match...');

			// processFace is a simple API call - no container or callbacks needed
			const response = await incode.processFace({
				token: onboardingToken,
			});

			console.log('processFace response = ', response);
			console.log('Confidence: ', response.confidence);
			console.log('Existing user: ', response.existingUser);

			setIsProcessing(false);
			hasStartedRef.current = false;
			onComplete({
				success: true,
				stage: 'selfie',
				confidence: response.confidence,
				existingUser: response.existingUser,
			});
		} catch (err: any) {
			console.log('processFace error = ', err);
			setIsProcessing(false);
			hasStartedRef.current = false;
			hasCompletedRef.current = false;
			setMessage('Face processing failed. Please try again.');
			onComplete({
				success: false,
				stage: 'selfie',
				error: err,
			});
		}
	};
	return (
		<div className="selfie-wrapper d-flex flex-column align-items-center justify-content-center w-100">
			<div className="camera-container d-flex flex-column align-items-center w-100">
				<div className="camera-frame w-100 d-flex justify-content-center">
					<div
						ref={containerRef}
						className="incode-camera-host d-flex align-items-center justify-content-center position-relative">
						{/* Loading handled by DisplayPage */}
					</div>
				</div>

				<div className="text-center mt-3 small fw-semibold text-dark">
					{/* Align your face in the center of the frame */}
				</div>
			</div>
		</div>
	);
};

export default CaptureFace;
